import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import {
  BarChart, Bar, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { AXIS } from './chartTheme';
import { AreaGradient } from './ChartKit';

/**
 * Seccion Turnos rehecha como "carga del taller": volumen y tendencia,
 * dias y horarios pico, estados, reclamos y carga por mecanico.
 */

function DeltaChip({ value }: { value: number | null }) {
  if (value == null) return <span className="text-xs text-muted-foreground">sin ref.</span>;
  const color = value > 0 ? 'text-green-600 bg-green-500/10' : value < 0 ? 'text-red-600 bg-red-500/10' : 'text-muted-foreground bg-muted';
  return <span className={`text-xs px-1.5 py-0.5 rounded ${color}`}>{value > 0 ? '▲ +' : value < 0 ? '▼ ' : ''}{value}% vs per. ant.</span>;
}

const STATUS_LABEL: Record<string, string> = {
  completed: 'Completadas', in_progress: 'En progreso', pending: 'Pendientes',
  confirmed: 'Confirmadas', cancelled: 'Canceladas', no_show: 'Ausencias'
};

export default function TurnosSection({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    setError('');
    setLoading(true);
    axios.get('/api/reports/tablero/turnos', { params: { startDate, endDate } })
      .then((r) => setD(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando turnos'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading && !d) return <div className="py-10 text-center text-muted-foreground">Cargando turnos...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!d) return null;

  // Franjas de 15/30 min agrupadas por HORA: como barras individuales el
  // gráfico quedaba ilegible (una barra por franja con etiquetas amontonadas).
  const porHoraAgrupado = (() => {
    const map = new Map<string, number>();
    (d.porHora || []).forEach((h: any) => {
      const m = String(h.slot || '').match(/^(\d{1,2})/);
      if (!m) return; // 'sin horario' queda fuera del gráfico
      const hora = m[1].padStart(2, '0');
      map.set(hora, (map.get(hora) || 0) + Number(h.n || 0));
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([hora, n]) => ({ hora: `${hora}h`, n }));
  })();

  return (
    <div className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">OTs en el período</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.ots.value}</p>
          <DeltaChip value={d.kpis.ots.deltaPct} />
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Completadas</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.completadas.value} <span className="text-sm text-muted-foreground font-normal">({d.kpis.completadas.pctSobreTotal}%)</span></p>
          <span className="text-xs text-muted-foreground">del total del período</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Ausencias</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.noShow.value}</p>
          <span className="text-xs text-muted-foreground">turnos sin presentarse</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Reclamos</p>
          <p className={`text-2xl font-semibold leading-none mb-1.5 ${d.kpis.reclamos.value > 0 ? 'text-red-600' : ''}`}>{d.kpis.reclamos.value} <span className="text-sm text-muted-foreground font-normal">({d.kpis.reclamos.tasa}%)</span></p>
          <span className="text-xs text-muted-foreground">OTs que reabren un trabajo</span>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="pt-5">
          <h3 className="font-semibold text-sm mb-1">OTs por mes</h3>
          <p className="text-xs text-muted-foreground mb-3">Volumen de trabajo del taller (verde) y reclamos (rojo)</p>
          <ChartContainer
            config={{
              ots: { label: 'OTs', color: 'hsl(var(--chart-2))' },
              reclamos: { label: 'Reclamos', color: '#E24B4A' }
            }}
            className="h-[230px] w-full"
          >
            <ComposedChart data={d.serie} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <AreaGradient id="fillOts" color="var(--color-ots)" />
              </defs>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
              <XAxis dataKey="label" {...AXIS} />
              <YAxis {...AXIS} width={30} allowDecimals={false} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Area type="monotone" dataKey="ots" stroke="var(--color-ots)" strokeWidth={2.4} fill="url(#fillOts)" dot={{ r: 3 }} />
              <Line type="monotone" dataKey="reclamos" stroke="var(--color-reclamos)" strokeWidth={1.6} dot={{ r: 2 }} />
            </ComposedChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-1">OTs por día de semana</h3>
            <p className="text-xs text-muted-foreground mb-3">¿Cuándo está más cargado el taller? (histórico completo)</p>
            <ChartContainer config={{ ots: { label: 'OTs', color: 'hsl(var(--chart-3))' } }} className="h-[210px] w-full">
              <BarChart data={d.porDiaSemana} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
                <XAxis dataKey="dia" {...AXIS} />
                <YAxis {...AXIS} width={30} allowDecimals={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="ots" fill="var(--color-ots)" radius={[3, 3, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-1">Franjas horarias</h3>
            <p className="text-xs text-muted-foreground mb-3">Demanda por horario (histórico completo)</p>
            <ChartContainer config={{ n: { label: 'OTs', color: 'hsl(var(--chart-4))' } }} className="h-[210px] w-full">
              <ComposedChart data={porHoraAgrupado} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
                <XAxis dataKey="hora" {...AXIS} />
                <YAxis {...AXIS} width={30} allowDecimals={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Area type="monotone" dataKey="n" stroke="var(--color-n)" strokeWidth={2} fill="var(--color-n)" fillOpacity={0.2} dot={false} />
              </ComposedChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Estados del período</h3>
            {d.estados.length === 0 && <p className="text-sm text-muted-foreground">Sin OTs en el período</p>}
            <div className="space-y-2">
              {d.estados.map((e: any) => {
                const max = Math.max(1, ...d.estados.map((x: any) => x.n));
                return (
                  <div key={e.status}>
                    <div className="flex items-baseline justify-between text-sm mb-0.5">
                      <span>{STATUS_LABEL[e.status] || e.status}</span>
                      <span className="text-muted-foreground text-xs">{e.n}</span>
                    </div>
                    <div className="w-full h-1.5 rounded bg-muted overflow-hidden">
                      <div className="h-full rounded" style={{ width: `${(e.n / max) * 100}%`, background: e.status === 'completed' ? '#1baf7a' : e.status === 'cancelled' || e.status === 'no_show' ? '#E24B4A' : '#EF9F27' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Carga por mecánico (período)</h3>
            {d.porMecanico.length === 0 && <p className="text-sm text-muted-foreground">Sin OTs asignadas en el período</p>}
            <div className="space-y-2">
              {d.porMecanico.map((m: any) => {
                const max = Math.max(1, ...d.porMecanico.map((x: any) => x.n));
                return (
                  <div key={m.mecanico}>
                    <div className="flex items-baseline justify-between text-sm mb-0.5">
                      <span className="truncate max-w-[200px]">{m.mecanico}</span>
                      <span className="text-muted-foreground text-xs">{m.n}</span>
                    </div>
                    <div className="w-full h-1.5 rounded bg-muted overflow-hidden">
                      <div className="h-full rounded bg-primary/70" style={{ width: `${(m.n / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
